import { FC, useEffect, useRef, useState } from 'react';

import {
  isThanksPrintBoxSet,
  ThanksPrintBox,
  ThanksPrintTemplate,
  ThanksPrintTextAlign,
} from '@shared/api/giftItemBrand/giftItemBrand.interface';
import { Image } from '@shared/api/media/media.interface';
import { fileBucketUrl } from '@shared/utils/fileUtils';

/** fontScale 미설정 시 기본 글씨 크기 (기존 대비 1.25배) */
const PREVIEW_FONT_SIZE = 'clamp(10px, 3.75vw, 19px)';

/** 0~1 박스 비율 → 오버레이 절대 위치 스타일 */
const boxStyle = (box: ThanksPrintBox): React.CSSProperties => ({
  left: `${Math.min(box.x0, box.x1) * 100}%`,
  top: `${Math.min(box.y0, box.y1) * 100}%`,
  width: `${Math.abs(box.x1 - box.x0) * 100}%`,
  height: `${Math.abs(box.y1 - box.y0) * 100}%`,
});

/** align → flex 정렬 + text-align */
const alignToCss = (
  align?: ThanksPrintTextAlign,
): { justify: string; textAlign: 'left' | 'center' | 'right' } => {
  switch (align) {
    case ThanksPrintTextAlign.Left:
      return { justify: 'justify-start', textAlign: 'left' };
    case ThanksPrintTextAlign.Right:
      return { justify: 'justify-end', textAlign: 'right' };
    default:
      return { justify: 'justify-center', textAlign: 'center' };
  }
};

/** 템플릿 이미지 위 텍스트 오버레이 (인쇄 미리보기) */
const BoxOverlay: FC<{
  box: ThanksPrintBox;
  text: string;
  bold?: boolean;
  underline?: boolean;
  /** fontScale 계산값 (px). 없으면 기본 크기 */
  fontSize?: string;
}> = function BoxOverlay({ box, text, bold, underline, fontSize }) {
  const a = alignToCss(box.align);
  return (
    <div
      className={`absolute flex items-center ${a.justify} overflow-hidden p-1`}
      style={boxStyle(box)}
    >
      <span
        className={`noto-serif-kr whitespace-pre leading-snug text-gray-900 ${
          bold ? 'font-semibold' : 'font-medium'
        } ${underline ? 'underline decoration-1 underline-offset-4' : ''}`}
        style={{
          fontSize: fontSize ?? PREVIEW_FONT_SIZE,
          textAlign: a.textAlign,
          // 템플릿 지정 색상 — 미설정이면 클래스 기본색(text-gray-900)
          ...(box.colorHex && { color: box.colorHex }),
        }}
      >
        {text}
      </span>
    </div>
  );
};

interface GiftThanksPrintPreviewProps {
  /** 미리보기에 사용할 템플릿 (디자인) */
  template?: ThanksPrintTemplate<Image>;
  /** 인쇄될 감사글 */
  message: string;
  /** 인쇄될 보내는 이 ("홍길동 배상") */
  sender: string;
  /** 추가 클래스 (래퍼) */
  className?: string;
  onTouchStart?: (e: React.TouchEvent) => void;
  onTouchMove?: (e: React.TouchEvent) => void;
  onTouchEnd?: () => void;
}

/**
 * 감사 메시지 인쇄 완성 미리보기 — 템플릿 이미지 위에 감사글/보내는이를 오버레이.
 * 상세페이지 편집(GiftThanksPrintSection)과 주문/결제 요약(GiftThanksPrintSummary)에서 공통 사용.
 */
export const GiftThanksPrintPreview: FC<GiftThanksPrintPreviewProps> =
  function GiftThanksPrintPreview({
    template,
    message,
    sender,
    className,
    onTouchStart,
    onTouchMove,
    onTouchEnd,
  }) {
    const previewUrl = template?.templateImage
      ? fileBucketUrl(template.templateImage.url, 'raw')
      : '';

    // fontScale용 — 표시된 이미지의 실제 세로높이 추적 (반응형·회전 대응)
    const imgRef = useRef<HTMLImageElement>(null);
    const [imgHeight, setImgHeight] = useState(0);
    useEffect(() => {
      const el = imgRef.current;
      if (!el || typeof ResizeObserver === 'undefined') return undefined;
      const observer = new ResizeObserver(() => setImgHeight(el.clientHeight));
      observer.observe(el);
      return () => observer.disconnect();
    }, [previewUrl]);

    // 글자 크기 = 이미지 세로높이 × fontScale (템플릿 단위, message/sender 공통)
    const scaledFontSize =
      template?.fontScale && template.fontScale > 0 && imgHeight > 0
        ? `${imgHeight * template.fontScale}px`
        : undefined;

    const messageBox = template?.messageBox;
    const senderBox = template?.senderBox;

    return (
      <div
        className={`relative w-full select-none overflow-hidden rounded-xl border border-stone-200 bg-stone-50 ${
          className ?? ''
        }`}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        {previewUrl ? (
          <img
            ref={imgRef}
            src={previewUrl}
            alt={template?.name ?? '감사인쇄 템플릿'}
            className="pointer-events-none w-full select-none"
            draggable={false}
            onLoad={() => setImgHeight(imgRef.current?.clientHeight ?? 0)}
          />
        ) : (
          <div className="flex aspect-[4/3] items-center justify-center text-sm text-gray-400">
            미리보기 이미지가 없습니다
          </div>
        )}

        {/* 좌표 없는 유령 박스({align:'center'})는 렌더하지 않음 — NaN 위치 방지 */}
        {isThanksPrintBoxSet(messageBox) && (
          <BoxOverlay box={messageBox} text={message} fontSize={scaledFontSize} />
        )}
        {isThanksPrintBoxSet(senderBox) && (
          <BoxOverlay
            box={senderBox}
            text={sender}
            bold
            // 미설정 = true (기존 항상 밑줄과 동일), false일 때만 밑줄 제거
            underline={senderBox.underline !== false}
            fontSize={scaledFontSize}
          />
        )}
      </div>
    );
  };
